import { Capacitor } from '@capacitor/core'
import { App as CapacitorApp } from '@capacitor/app'
import type { PluginListenerHandle } from '@capacitor/core'
import * as Sentry from '@sentry/vue'

export interface MercureOptions {
  /** Mercure hub URL. Defaults to VITE_MERCURE_HUB_URL env var. */
  hubUrl?: string
  /** The Mercure topic to subscribe to */
  topic: string
  /** The Mercure subscriber JWT token */
  token: string
  /** Called with parsed JSON payload on each message */
  onMessage: (data: unknown) => void
  /**
   * Called when the token appears expired (EventSource error after connect).
   * Should fetch a fresh token and call reconnect(), or return a new token string.
   * If omitted, the composable simply logs a warning.
   */
  onTokenExpired?: () => Promise<string | void>
  /**
   * Called every time the EventSource successfully (re)connects, including the
   * very first connect — use it to resync any state that may have drifted
   * while disconnected (Mercure does not replay missed messages).
   */
  onReconnect?: () => void | Promise<void>
}

export interface MercureHandle {
  /** Open the EventSource connection */
  connect: () => void
  /** Close the EventSource connection */
  disconnect: () => void
}

/**
 * Creates a managed Mercure SSE subscription.
 *
 * Design decisions (DEVAMS-1703):
 * - Authorization is passed as a query param (not a header) for Capacitor compatibility,
 *   since EventSource does not support custom headers in native environments.
 * - Capacitor app pause/restore events are used to cleanly disconnect/reconnect,
 *   preventing stale connections when the app is backgrounded on mobile.
 * - Token refresh is delegated to the caller via onTokenExpired() to keep the
 *   composable decoupled from specific auth strategies.
 * - This composable does NOT manage lifecycle (onMounted/onUnmounted) by itself;
 *   the caller is responsible, enabling use in both components and stores.
 */
export function useMercure(options: MercureOptions): MercureHandle {
  let eventSource: EventSource | null = null
  let currentToken = options.token
  let pauseHandle: PluginListenerHandle | null = null
  let resumeHandle: PluginListenerHandle | null = null
  // Track whether a reconnect was triggered from a resume so we don't loop
  let isReconnecting = false

  const hubUrl = options.hubUrl ?? import.meta.env.VITE_MERCURE_HUB_URL ?? 'https://mro-ops-api.nayak.aero/.well-known/mercure'

  function buildUrl(token: string): string {
    const url = new URL(hubUrl)
    url.searchParams.set('topic', options.topic)
    url.searchParams.set('authorization', token)
    return url.toString()
  }

  function close(): void {
    if (eventSource) {
      eventSource.close()
      eventSource = null
    }
  }

  async function handleError(): Promise<void> {
    // EventSource readyState 2 = CLOSED (permanent failure, not just a retry)
    // Browsers auto-retry on transient failures; we only act on permanent close.
    if (eventSource?.readyState === EventSource.CLOSED) {
      console.warn(`[Mercure] Connection closed for topic: ${options.topic}. Attempting token refresh.`)
      close()

      if (options.onTokenExpired) {
        try {
          const newToken = await options.onTokenExpired()
          if (newToken) {
            currentToken = newToken
          }
          open()
        } catch (err) {
          Sentry.captureException(err, {
            tags: { feature: 'mercure', topic: options.topic },
          })
          console.error('[Mercure] Token refresh failed:', err)
        }
      }
    }
  }

  function open(): void {
    close()
    const url = buildUrl(currentToken)
    console.log(`[Mercure] Connecting to topic: ${options.topic}`)
    eventSource = new EventSource(url)

    eventSource.onopen = () => {
      console.log(`[Mercure] Connected to topic: ${options.topic}`)
      isReconnecting = false
      Promise.resolve(options.onReconnect?.()).catch((err) => {
        Sentry.captureException(err, {
          tags: { feature: 'mercure', topic: options.topic },
        })
      })
    }

    eventSource.onmessage = (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data)
        options.onMessage(data)
      } catch (err) {
        console.error('[Mercure] Failed to parse message:', err, event.data)
      }
    }

    eventSource.onerror = () => {
      handleError()
    }
  }

  async function setupCapacitorLifecycle(): Promise<void> {
    if (!Capacitor.isNativePlatform()) return

    pauseHandle = await CapacitorApp.addListener('appStateChange', (state) => {
      if (!state.isActive) {
        console.log(`[Mercure] App paused — disconnecting topic: ${options.topic}`)
        close()
      } else if (!isReconnecting) {
        console.log(`[Mercure] App resumed — reconnecting topic: ${options.topic}`)
        isReconnecting = true
        // If caller provides a token refresh, use it on resume (token may have expired while backgrounded)
        if (options.onTokenExpired) {
          options.onTokenExpired()
            .then((newToken) => {
              if (newToken) currentToken = newToken
              open()
            })
            .catch((err) => {
              Sentry.captureException(err, {
                tags: { feature: 'mercure', topic: options.topic },
              })
              // Fall back to existing token
              open()
            })
        } else {
          open()
        }
      }
    })
  }

  function removeCapacitorListeners(): void {
    pauseHandle?.remove()
    resumeHandle?.remove()
    pauseHandle = null
    resumeHandle = null
  }

  return {
    connect() {
      setupCapacitorLifecycle()
      open()
    },
    disconnect() {
      close()
      removeCapacitorListeners()
    },
  }
}
